import {
  Tab as MUITab,
  TabList as MUITabList,
  TabPanel as MUITabPanel,
  Tabs as MUITabs,
} from '@mui/joy';
import classNames from 'classnames';

import {
  type DefaultElementTypes,
  type TabListProps,
  type TabPanelProps,
  type TabProps,
  type TabsProps,
  type ValueType,
} from './Tabs.types';

import { DATA_ATTRIBUTES, DEFAULT_CLASSES, VARIANTS, VARIANTS_MUI_COLORS_MAP } from './Tabs.data';

const Tab = <T extends React.ElementType = DefaultElementTypes['Tab']>({
  active,
  as,
  className,
  ...rest
}: TabProps<T>) => {
  const variant = (() => {
    if (typeof active !== 'boolean') return null;

    return active ? VARIANTS.ACTIVE : VARIANTS.DEFAULT;
  })();

  const variantProps = {
    [DATA_ATTRIBUTES.VARIANT]: variant,
    color: variant ? VARIANTS_MUI_COLORS_MAP[variant] : undefined,
  };

  return (
    <MUITab
      {...rest}
      {...variantProps}
      className={classNames(DEFAULT_CLASSES.TAB, className)}
      component={as}
      role="tab"
    />
  );
};

const TabList = <T extends React.ElementType = DefaultElementTypes['TabList']>({
  as,
  className,
  ...rest
}: TabListProps<T>) => (
  <MUITabList
    {...rest}
    className={classNames(DEFAULT_CLASSES.TAB_LIST, className)}
    component={as}
    role="tablist"
  />
);

const TabPanel = <T extends React.ElementType = DefaultElementTypes['TabPanel']>({
  as,
  className,
  ...rest
}: TabPanelProps<T>) => (
  <MUITabPanel
    {...rest}
    className={classNames(DEFAULT_CLASSES.TAB_PANEL, className)}
    component={as}
    role="tabpanel"
  />
);

const TabsRoot = <
  TElement extends React.ElementType = DefaultElementTypes['Tabs'],
  TValue extends ValueType = ValueType,
>({
  as,
  className,
  onChange,
  ...rest
}: TabsProps<TElement, TValue>) => (
  <MUITabs
    {...rest}
    className={classNames(DEFAULT_CLASSES.TABS, className)}
    component={as}
    onChange={(_, value) => onChange?.(value as TValue | null)}
  />
);

const Tabs = Object.assign(TabsRoot, {
  List: TabList,
  Panel: TabPanel,
  Tab,
});

export default Tabs;
